import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserRound,
  Receipt,
  Route,
  ChefHat,
  UtensilsCrossed,
  Landmark,
  Clock,
  Tag,
  ListFilter,
  LifeBuoy,
  ShoppingBag,
  Settings,
} from "lucide-react";
import "./AdminPage.scss";
import {
  OverviewSection,
  UsersSection,
  RoutesSection,
  MenuItemsSection,
  DailyMenuSection,
  DashboardSection,
  DebtManagementSection,
  DeliverySchedulesSection,
  CustomersSection,
  PriceListsSection,
  PriceRatesSection,
  SupportTicketsSection,
  OnlineOrdersSection,
} from "./sections";
import { useAuth } from "../../context/AuthContext";
import { SharedSidebar } from "../../components";

const ADMIN_MENU_ITEMS = [
  { key: "support-tickets", label: "Hlásenia chýb", icon: LifeBuoy, roles: ["SUPERADMIN"] },
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["ADMIN", "SUPERADMIN", "MANAGER"] },
  { key: "overview", label: "Prehľad", icon: Receipt, roles: ["ADMIN", "SUPERADMIN", "MANAGER"] },
  { key: "customers", label: "Zákazníci", icon: UserRound, roles: ["ADMIN", "SUPERADMIN"] },
  { key: "debts", label: "Správa dlhov", icon: Landmark, roles: ["ADMIN", "SUPERADMIN", "MANAGER"] },
  { key: "users", label: "Správa užívateľov", icon: Users, roles: ["ADMIN", "SUPERADMIN"] },
  { key: "routes", label: "Správa trás", icon: Route, roles: ["ADMIN", "SUPERADMIN"] },
  { key: "menu-items", label: "Správa jedál", icon: ChefHat, roles: ["ADMIN", "SUPERADMIN", "MANAGER"] },
  { key: "daily-menu", label: "Správa obedového menu", icon: UtensilsCrossed, roles: ["ADMIN", "SUPERADMIN", "MANAGER"] },
  {
    key: "pricing",
    label: "Správa cenníkov",
    icon: Tag,
    roles: ["ADMIN", "SUPERADMIN"],
    children: [
      { key: "pricing-lists", label: "Zoznam cenníkov", icon: ListFilter, roles: ["ADMIN", "SUPERADMIN"] },
      { key: "pricing-rates", label: "Ceny a sadzby", icon: Receipt, roles: ["ADMIN", "SUPERADMIN"] },
    ],
  },
  /*
   * Nastavenia prevádzky.
   *
   * „Online objednávky“ na hlavnej úrovni znelo, akoby sa pod tým vypisovali
   * webové objednávky — pritom je to nastavenie. Spolu s časom prijímania
   * objednávok ide o tú istú vec z dvoch strán: dokedy sa dá objednať a či
   * sa dá objednať cez web. Preto sú pod jednou položkou.
   */
  {
    key: "settings",
    label: "Nastavenia prevádzky",
    icon: Settings,
    roles: ["ADMIN", "SUPERADMIN", "MANAGER"],
    children: [
      // R29: `delivery_schedules` nikdy neboli "casy rozvozov", ale cas,
      // dokedy prevadzka berie objednavky -- a prave podla neho sa pocita
      // okno webovych objednavok.
      { key: "delivery-schedules", label: "Čas prijímania objednávok", icon: Clock, roles: ["ADMIN", "SUPERADMIN"] },
      { key: "online-orders", label: "Objednávanie cez web", icon: ShoppingBag, roles: ["ADMIN", "SUPERADMIN", "MANAGER"] },
    ],
  },
];

function AdminPage({ initialSection = "dashboard" }) {
  const { userRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState(
    location.pathname.startsWith("/customers")
      ? "customers"
      : location.pathname.startsWith("/admin/cenniky/zoznam")
        ? "pricing-lists"
        : location.pathname.startsWith("/admin/cenniky/ceny")
          ? "pricing-rates"
          : (location.state?.section || initialSection)
  );

  useEffect(() => {
    if (location.pathname.startsWith("/customers")) {
      setActiveSection("customers");
      return;
    }

    if (location.pathname.startsWith("/admin/cenniky/zoznam")) {
      setActiveSection("pricing-lists");
      return;
    }

    if (location.pathname.startsWith("/admin/cenniky/ceny")) {
      setActiveSection("pricing-rates");
      return;
    }
  }, [location.pathname]);

  /*
   * Rola rozhoduje aj o PODPOLOŽKÁCH, nielen o skupine.
   *
   * „Nastavenia prevádzky“ vidí aj manažér (objednávanie cez web si zapína),
   * ale čas prijímania objednávok mu nepatrí. Bez tohto filtra by skupina
   * jej viditeľnosť podpoložkám požičala — server by ich síce odmietol, ale
   * manažér by videl obrazovku, ktorá mu nikdy nebude fungovať.
   */
  const visibleMenuItems = ADMIN_MENU_ITEMS
    .filter((item) => item.roles.includes(userRole))
    .map((item) => (item.children
      ? { ...item, children: item.children.filter((child) => child.roles.includes(userRole)) }
      : item))
    .filter((item) => !item.children || item.children.length > 0);
  const panelTitle = userRole === "MANAGER" ? "MANAGER PANEL" : "ADMIN PANEL";

  const handleSectionSelect = (key) => {
    if (key === "customers") {
      setActiveSection("customers");
      navigate("/customers");
      return;
    }

    if (key === "pricing-lists") {
      setActiveSection("pricing-lists");
      navigate("/admin/cenniky/zoznam");
      return;
    }

    if (key === "pricing-rates") {
      setActiveSection("pricing-rates");
      navigate("/admin/cenniky/ceny");
      return;
    }

    if (location.pathname.startsWith("/customers")) {
      navigate("/admin", { state: { section: key } });
      return;
    }

    setActiveSection(key);
  };

  const renderContent = () => {
    switch (activeSection) {
      case "support-tickets":
        return <SupportTicketsSection />;
      case "dashboard":
        return <DashboardSection />;
      case "overview":
        return <OverviewSection />;
      case "users":
        return <UsersSection />;
      case "routes":
        return <RoutesSection />;
      case "menu-items":
        return <MenuItemsSection />;
      case "daily-menu":
        return <DailyMenuSection />;
      case "delivery-schedules":
        return <DeliverySchedulesSection />;
      case "online-orders":
        return <OnlineOrdersSection />;
      case "debts":
        return <DebtManagementSection />;
      case "customers":
        return <CustomersSection />;
      case "pricing-lists":
        return <PriceListsSection />;
      case "pricing-rates":
        return <PriceRatesSection />;
      default:
        return <DashboardSection />;
    }
  };

  return (
    <div className="admin">
      <SharedSidebar
        title={panelTitle}
        items={visibleMenuItems}
        activeKey={activeSection}
        onSelect={handleSectionSelect}
      />

      <main className="admin__content">{renderContent()}</main>
    </div>
  );
}

export default AdminPage;
